<template>
	<div class="col" :class="{ 'd-none': disabled }">
		<DropdownInput
			:label="content.flight_leaving_from"
			:placeholder="content.flight_station_or_airport"
			:value="getOriginLabel(selectedCode)"
			:dropdownOpen="dropdownOpen"
			@close="closeDropdown"
			@open="openDropdown"
			@search="handleSearch"
			:disabled="disabled"
		>
			<template #dropdown>
				<!-- Mobile view heading + search -->
				<div class="d-md-none d-block">
					<div class="col-12 d-flex flex-row nowrap justify-content-between mb-4">
						<span class="heading text-start text-grey-900">{{ content.flight_leaving_from }}</span>
						<ClsBtn @click="closeDropdown" />
					</div>
					<DropdownInput
						ref="dropdownInputRef"
						:label="content.flight_leaving_from"
						:placeholder="content.flight_station_or_airport"
						:value="getOriginLabel(selectedCode)"
						:dropdownOpen="false"
						@close="closeDropdown"
						@open="openDropdown"
						@search="handleSearch"
					/>
				</div>

				<!-- List -->
				<div class="inner-wrapper position-relative">
					<div
						class="px-2"
						v-for="(group) in filteredFlightOrigins"
						:key="group.key"
					>
						<!-- Title -->
						<!-- <div
							v-if="group.showTitle && group.codes && group.codes.length > 0"
							class="caption-strong text-primary-blue mb-1 mt-1"
						>
							{{ group.type === 'train' ? content.flight_by_train : content.flight_by_plane }}
						</div> -->

						<!-- Parent Option -->
						<div
							class="mb-2 cursor-pointer option px-3 py-2"
							:class="isSelected(group.key) && 'selected-option'"
							@click="() => handleSelect(group.key)"
							v-if="group.name"
						>
							<div class="d-flex align-items-center gap-2 justify-content-between">
								<div class="d-flex min-w-0">
									<span class="text-primary me-2 flex-shrink-0">
										<svg width="20" height="20" class="icon text-primary-blue">
											<use
												:xlink:href="group.type === 'train'
													? '#___icon___train'
													: '#___icon___flight'"
											/>
										</svg>
									</span>
									<div class="min-w-0">
										<div class="caption-strong text-grey-900">
											{{ group.name }}
											<span v-if="group.description" class="caption text-grey-700 fw-normal"> ({{ group.description }})</span>
										</div>
										<div class="caption text-grey-500">{{ group.brandName || group.codes.join(",") }}</div>
									</div>
								</div>
								<span class="btn-close-sm ms-2 flex-shrink-0" v-if="isSelected(group.key)">
									<img src="https://www.uk.travelski.com/searchbox/img/svg/close.svg" width="18">
								</span>
							</div>
						</div>

						<!-- Children -->
							
						<template v-if="group.children">
							<div
								v-for="child in group.children"
								:key="child.key"
								class="ms-4 mb-2 cursor-pointer option px-3 py-2"
								:class="isSelected(child.key) && 'selected-option'"
								@click="() => handleSelect(child.key)"
							>
								<div class="d-flex align-items-center gap-2 justify-content-between">
									<div class="d-flex">
										<span class="text-primary me-2">
											<svg width="20" height="20" class="icon text-primary-blue">
												<use xlink:href="#___icon___flight" />
											</svg>
										</span>
										<div>
											<div class="caption-strong text-grey-900">{{ child.name }}</div>
											<div class="caption text-grey-500">{{ child.codes.join(",") }}</div>
										</div>
									</div>
									<span class="btn-close-sm ms-2" v-if="isSelected(child.key)">
										<img src="https://www.uk.travelski.com/searchbox/img/svg/close.svg" width="18">
									</span>
								</div>
							</div>
						</template>
					</div>

					<div class="px-3" v-if="!filteredFlightOrigins.length && searchQuery.length >= 3">
						<NoResultsSearch  />
					</div>

					<!-- <p class="no-results">
						{{ content.flight_no_origins }}
					</p> -->

					<div class="form-check position-sticky bottom-0 bg-white d-flex gap-2 m-0 ps-3 py-3 origin-no-transport">
						<input
							class="form-check-input"
							type="checkbox"
							id="noTransport"
							v-model="checked"
						/>
						<label class="form-check-label" for="noTransport">
							{{ content.flight_switch_no }}
						</label>
					</div>
				</div>

				<!-- Confirm Button (only on mobile) -->
				<div class="d-flex justify-content-end mt-3 px-3 d-md-none">
					<button
						class="button-primary primary-normal p-2 mt-2 px-3 font-font-weight-600 font-size-16 cursor-pointer w-100 d-flex align-items-center justify-content-center py-3 py-md-2 px-4"
						@click="handleConfirm"
						:disabled="!selectedCode"
					>
						{{ content.flight_confirm }}
					</button>
				</div>
			</template>
		</DropdownInput>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue"
import { useStore } from "vuex"
import { airportCodeMap } from "@/config/airportCodeMap"
import { trainStationCodeMap } from "@/config/trainStationCodeMap"
import { transportPacks } from "@/config/transportPackagePriorities"
import DropdownInput from "./dropdownInput.vue"
import ClsBtn from '@/vue/common/clsBtn.vue'
import screenType from "@/vue/planning/computed/screenType"
import NoResultsSearch from "./NoResultsSearch.vue"
import flightPackCode from "@/config/flightPackCode"
import trainPackCode from "@/config/trainPackCode"
import {
	getAllowedOriginsForTrainPack,
	findTrainPrestIdForOrigin,
	isEurostarOrigin,
	isNativeEurostarPack,
	isNlWebsite,
	isUkWebsite,
	resolveTrainPackCodeForOrigin,
} from "@/config/trainPackageConfig"

const store = useStore()

interface FlightOriginGroup {
	name: string
	codes: string[]
	key: string
	type: "train" | "flight"
	showTitle: boolean
	brandName?: string
	description?: string
	children?: FlightOriginGroup[]
}

const dropdownOpen = ref(false)
const checked = ref(false)
const selectedCode = ref<string>(store.state.flightOrigin)
const searchQuery = ref("")
const filteredFlightOrigins = ref<FlightOriginGroup[]>([])

// Template ref to access the DropdownInput component
const dropdownInputRef = ref<InstanceType<typeof DropdownInput> | null>(null)

const content = computed(() => store.state.content)
const disabled = computed(() => !transportPacks.includes(store.state.packCode))

const groupedTransportOrigins = computed<FlightOriginGroup[]>(() => {
	const allCodes = [...new Set(store.getters.flightOrigins)] as string[]
	const groups: FlightOriginGroup[] = []
	const seenCodes = new Set<string>()
	const titleFlags = { train: false, flight: false }

	if (trainPackCode.includes(store.state.packCode)) {
		if (store.getters.availableTrainPackCodes.length) {
			const allowed = getAllowedOriginsForTrainPack(store.state.packCode)
			// Only origins present on availability prestations (stored in flightOrigins).
			const originCodes = ([...new Set(store.getters.flightOrigins)] as string[])
				.filter((code) => trainStationCodeMap.has(code))
				.filter((code) => !allowed || allowed.includes(code))

			const getStationName = (code: string) =>
				store.state.content?.["planning_traintimeline_station"]?.[code]?.name ||
				trainStationCodeMap.get(code)?.name ||
				code

			// Eurostar first, then TNE; within each group sort by display name ASC
			originCodes.sort((a, b) => {
				const aEurostar = isEurostarOrigin(a) ? 0 : 1
				const bEurostar = isEurostarOrigin(b) ? 0 : 1
				if (aEurostar !== bEurostar) return aEurostar - bEurostar
				return getStationName(a).localeCompare(getStationName(b), undefined, { sensitivity: "base" })
			})

			const trainGroups = originCodes.map((code) => {
				const trainLabels = isEurostarOrigin(code)
					? content.value?.planning_eurostar
					: content.value?.planning_tne
				return {
					name: getStationName(code),
					codes: [code],
					key: code,
					type: "train" as const,
					showTitle: !titleFlags.train && (titleFlags.train = true),
					brandName: trainLabels?.name || "",
					description: trainLabels?.description || "",
				}
			})
			groups.push(...trainGroups)
		}
	}
	else if (flightPackCode.includes(store.state.packCode)) {
		// Add remaining airports
		for (const code of allCodes) {
			if (seenCodes.has(code)) continue

			const currentCodeInfo = airportCodeMap.get(code);

			const name = store.state.content["planning_airport"][code]?.name || "";
			
			const relatedCodes = allCodes.filter(c => airportCodeMap.get(c)?.parent === code && !seenCodes.has(c))
			relatedCodes.forEach(c => seenCodes.add(c))

			if (!currentCodeInfo?.parent) {
				groups.push({
					name,
					codes: [code],//relatedCodes,
					key: code,//relatedCodes.join("-"),
					type: "flight",
					showTitle: !titleFlags.flight && (titleFlags.flight = true),
					children: relatedCodes.map(c => ({
						name: airportCodeMap.get(c)?.name || "",
						codes: [c],
						key: c,
						type: "flight",
						showTitle: false
					})),
				})
			}
		}
	} 
	return groups
})

function getOriginLabel(selectedOrigin: string): string {
	const found = groupedTransportOrigins.value.find(group => group.key === selectedOrigin)
	if (found) return found.name

	const origin = selectedOrigin?.split("-")[0] || ""
	if (!origin || origin === airportCodeMap.get("None")?.name) return ""

	if (trainPackCode.includes(store.state.packCode) || trainStationCodeMap.has(origin)) {
		return (
			store.state.content?.["planning_traintimeline_station"]?.[origin]?.name ||
			trainStationCodeMap.get(origin)?.name ||
			""
		)
	}
	if (flightPackCode.includes(store.state.packCode) || airportCodeMap.has(origin)) {
		return store.state.content[`planning_airport_${origin}`] || airportCodeMap.get(origin)?.name || ""
	}

	const packCode = store.state.packCode
	if (flightPackCode.includes(packCode)) {
		return airportCodeMap.get(origin)?.name || ""
	}
	if (trainPackCode.includes(packCode)) {
		return (
			store.state.content?.["planning_traintimeline_station"]?.[origin]?.name ||
			trainStationCodeMap.get(origin)?.name ||
			""
		)
	}
	return ""
}

// ✅ FIXED FUNCTION: strict equality match
function isSelected(code: string): boolean {
	return selectedCode.value === code
}

function handleSelect(code: string) {
	if (isSelected(code)) {
		handleUnselect()
		return
	}
	selectedCode.value = code
	if (window.innerWidth >= 1024) {
		store.commit("setFlightOrigin", code)
		//store.dispatch("setDefaultPackageForOriginType", code)
		closeDropdown()
	}
	handleSearch("")
}

function handleUnselect() {
	selectedCode.value = ""
	handleSearch("")
	store.commit("setFlightOrigin", airportCodeMap.get("None")?.name)
}

function handleConfirm() {
	if (selectedCode.value) {
		store.commit("setTransportv2", null)
		store.commit("setFlightOrigin", selectedCode.value)
		// store.dispatch("setDefaultPackageForOriginType", selectedCode.value)
		closeDropdown()
	}
}

function handleSearch(query: string) {
	searchQuery.value = query.toLowerCase()
	if (!query) {
		filteredFlightOrigins.value = groupedTransportOrigins.value
		return
	}

	filteredFlightOrigins.value = groupedTransportOrigins.value
		.map(group => {
			const matchGroup =
				group.name.toLowerCase().includes(searchQuery.value) ||
				(group.brandName || "").toLowerCase().includes(searchQuery.value) ||
				(group.description || "").toLowerCase().includes(searchQuery.value)
			const matchChildren = group.children?.filter(child =>
				child.name.toLowerCase().includes(searchQuery.value) ||
				child.codes.some(code => code.toLowerCase().includes(searchQuery.value))
			)

			if (matchGroup || (matchChildren && matchChildren.length)) {
				return {
					...group,
					children: matchChildren?.length ? matchChildren : group.children
				}
			}
			return null
		})
		.filter(Boolean) as FlightOriginGroup[]
}

function openDropdown() {
	dropdownOpen.value = true
}

function closeDropdown() {
	dropdownOpen.value = false
	selectedCode.value = store.state.flightOrigin || ''
	document.body.classList.remove("overflow-hidden")
}

async function focusInput() {
	await nextTick()
	if (dropdownInputRef.value?.inputRef) {
		dropdownInputRef.value.inputRef.focus()
	}
}

// const isTransportTypeChanged = (originStationCode: string, packCode: string) => {
// 	const isTransportTypeChanged = (flightPackCode.includes(packCode) && !airportCodeMap.has(originStationCode)) ||
// 		(trainPackCode.includes(packCode) && !trainStationCodeMap.has(originStationCode));
// 	return isTransportTypeChanged;
// };

// Watchers
watch(dropdownOpen, async (val) => {
	if (val) {
		filteredFlightOrigins.value = groupedTransportOrigins.value
		// Auto-focus on mobile when dropdown opens
		if (screenType.value === "mobile") {
			await focusInput()
		}
	}
})

watch(checked, async (val) => {
	store.commit("setTransportTrue", !val)
	const enableTransport = !val
	// flightSwitch already sets transport type + package; don't override with default flight pack
	if (enableTransport && store.getters.transportv2 != null) {
		closeDropdown()
		return
	}
	await store.dispatch("selectTransportWithDefaultPackage", enableTransport)
	closeDropdown()
})

watch(() => store.state.transportationSelected, val => {
	checked.value = !val
	
})

watch(() => store.getters.transportv2, () => {
	filteredFlightOrigins.value = groupedTransportOrigins.value
	selectedCode.value = store.state.flightOrigin || ""
})

watch(() => store.state.packCode, () => {
	selectedCode.value = store.state.flightOrigin || ""
	filteredFlightOrigins.value = groupedTransportOrigins.value
})

const openEurostarParticipantsLimitOnDesktop = (hadTooManyTravellers: boolean) => {
	if (!hadTooManyTravellers) return
	if (!(isUkWebsite() || isNlWebsite())) return
	if (store.state.screenType === "mobile") return
	const currentOrigin = store.state.flightOrigin?.split("-")[0] || ""
	if (!isEurostarOrigin(currentOrigin) || !isNativeEurostarPack(store.state.packCode)) return
	setTimeout(() => {
		store.dispatch("setParticipantsDropdownOpen", true)
	}, 0)
}

watch(() => store.state.flightOrigin, async (val) => {
	const originCode = val?.split("-")[0] || ""
	selectedCode.value = val
	const hadTooManyTravellers = store.state.participants.length > 9

	if (!originCode || originCode === airportCodeMap.get("None")?.name) {
		return
	}

	const packCode = store.state.packCode
	const isTrainPack = trainPackCode.includes(packCode)
	const isFlightPack = flightPackCode.includes(packCode)

	// Ignore mid-switch mismatches (airport on a train pack, or station on a flight pack).
	if (isTrainPack && !trainStationCodeMap.has(originCode)) return
	if (isFlightPack && !airportCodeMap.has(originCode)) return

	if (!isTrainPack) {
		await store.dispatch("setPackage", packCode)
		store.commit("setPackageId", store.state.packageId)
		return
	}

	const resolvedPack = resolveTrainPackCodeForOrigin(packCode, val)
	const availMap = store.state.prestMaps.seAvailabilities[resolvedPack]
	const originPrestId = findTrainPrestIdForOrigin(
		availMap ? Object.values(availMap) : [],
		resolvedPack,
		val
	)

	if (resolvedPack === packCode) {
		await store.dispatch("setPackage", packCode)
		store.commit("setPackageId", originPrestId || store.state.packageId)
		openEurostarParticipantsLimitOnDesktop(hadTooManyTravellers)
		return
	}

	const hashPackCode = (window.location.hash.match(/packCode=([^&]*)/) || [])[1]
	if (store.state.firstLoad && hashPackCode && hashPackCode === packCode) {
		return
	}

	const pack = store.getters.availablePackCodes.find(
		(availablePack: { codePackage: string; packageId?: string }) => availablePack.codePackage === resolvedPack
	)

	await store.dispatch("setPackage", resolvedPack)
	store.commit("setPackageId", originPrestId || pack?.packageId || store.state.packageId)
	openEurostarParticipantsLimitOnDesktop(hadTooManyTravellers)
})
</script>


<style scoped>
.inner-wrapper {
	width: 100%;
	max-height: 500px;
	overflow-x: hidden;
	overflow-y: auto;
	padding: 0;
	background: transparent;
	box-shadow: none;
	border: none;
}
.origin-no-transport {
	box-shadow: none;
	margin: 4px 0 0 !important;
	padding: 8px 12px 4px !important;
}
.option {
	border-radius: 8px;
}
.option:hover,
.selected-option {
	background-color: var(--blue-100);
}
.heading {
	font-weight: bold;
	font-size: 1.1rem;
}
.cursor-pointer {
	cursor: pointer;
}
.btn-close-sm {
	cursor: pointer;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}
@media(max-width: 1024px) {
	.inner-wrapper {
		box-shadow: none;
		max-height: 425px;
	}
}
</style>
